import React, { useId, useState } from "react";
import { Alert, Box, Button, IconButton, Stack, TextField } from "@mui/material";
import AddPhotoAlternateRounded from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { normalizeImageValue, resizeImageFile } from "../services/imageInput";

export default function ImageUploadField({
  label,
  value,
  onChange,
  multiple = false,
  messages,
  fullWidth = true,
}) {
  const inputId = useId();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const values = multiple
    ? (Array.isArray(value) ? value : []).filter(Boolean)
    : (value ? [value] : []);

  const emit = (next) => onChange?.({
    target: { value: multiple ? next : (next[0] || "") },
  });

  const addUrl = () => {
    const normalized = normalizeImageValue(multiple ? draft : value);
    if (normalized === null) {
      setError(messages.invalid);
      return;
    }
    setError("");
    if (multiple && normalized) {
      emit([...values, normalized]);
      setDraft("");
    } else {
      emit(normalized ? [normalized] : []);
    }
  };

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    try {
      const resized = await Promise.all(files.map((file) => resizeImageFile(file)));
      emit(multiple ? [...values, ...resized] : resized.slice(0, 1));
      setError("");
    } catch {
      setError(messages.invalidFile);
    }
  };

  return (
    <Stack spacing={1} sx={{ width: fullWidth ? "100%" : "auto" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
        <TextField
          fullWidth
          inputMode="url"
          label={label}
          value={multiple ? draft : (value || "")}
          onChange={(event) => {
            if (multiple) setDraft(event.target.value);
            else emit(event.target.value ? [event.target.value] : []);
          }}
          onBlur={() => {
            if (!multiple && value) addUrl();
          }}
          onKeyDown={(event) => {
            if (multiple && event.key === "Enter") {
              event.preventDefault();
              addUrl();
            }
          }}
          helperText={messages.hint}
          error={Boolean(error)}
        />
        {multiple && (
          <Button type="button" variant="outlined" onClick={addUrl} sx={{ minHeight: 40, whiteSpace: "nowrap" }}>
            {messages.addUrl}
          </Button>
        )}
        <Button
          component="label"
          htmlFor={inputId}
          variant="outlined"
          startIcon={<AddPhotoAlternateRounded />}
          sx={{ minHeight: 40, whiteSpace: "nowrap" }}
        >
          {messages.upload}
          <input
            id={inputId}
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple={multiple}
            onChange={upload}
          />
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {values.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {values.map((image, index) => (
            <Box key={`${image.slice(0, 40)}-${index}`} sx={{ position: "relative" }}>
              <Box
                component="img"
                src={image}
                alt={messages.preview.replace("{number}", String(index + 1))}
                sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 1, border: 1, borderColor: "divider" }}
              />
              <IconButton
                type="button"
                size="small"
                aria-label={messages.remove.replace("{number}", String(index + 1))}
                onClick={() => emit(values.filter((_item, itemIndex) => itemIndex !== index))}
                sx={{ position: "absolute", insetBlockStart: -8, insetInlineEnd: -8, bgcolor: "background.paper", boxShadow: 1 }}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
